import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getActiveCountries } from '../api/client';
import { 
    SUPPORTED_LANGUAGES, 
    GEO_STORAGE_KEY, 
    MANUAL_LOCK_KEY,
    applyDocumentAttributes,
    isSupportedLanguage,
    getBestSupportedLanguageForCountry,
    COUNTRY_DEFAULT_MAPPINGS
} from '../utils/countryLanguageEngine';
import { isRTL } from '../utils/translations';

export { SUPPORTED_LANGUAGES };

// Comprehensive Fallback Active Country Registry
export const DEFAULT_ACTIVE_COUNTRIES = [
    { countryCode: 'SA', countryName: 'Saudi Arabia', nativeName: 'المملكة العربية السعودية', defaultCurrency: 'SAR', defaultLanguage: 'ar', supportedLanguages: '["ar", "en"]', flagEmoji: '🇸🇦', region: 'Gulf' },
    { countryCode: 'AE', countryName: 'United Arab Emirates', nativeName: 'الإمارات العربية المتحدة', defaultCurrency: 'AED', defaultLanguage: 'ar', supportedLanguages: '["ar", "en"]', flagEmoji: '🇦🇪', region: 'Gulf' },
    { countryCode: 'QA', countryName: 'Qatar', nativeName: 'دولة قطر', defaultCurrency: 'QAR', defaultLanguage: 'ar', supportedLanguages: '["ar", "en"]', flagEmoji: '🇶🇦', region: 'Gulf' },
    { countryCode: 'KW', countryName: 'Kuwait', nativeName: 'دولة الكويت', defaultCurrency: 'KWD', defaultLanguage: 'ar', supportedLanguages: '["ar", "en"]', flagEmoji: '🇰🇼', region: 'Gulf' },
    { countryCode: 'BH', countryName: 'Bahrain', nativeName: 'مملكة البحرين', defaultCurrency: 'BHD', defaultLanguage: 'ar', supportedLanguages: '["ar", "en"]', flagEmoji: '🇧🇭', region: 'Gulf' },
    { countryCode: 'OM', countryName: 'Oman', nativeName: 'سلطنة عمان', defaultCurrency: 'OMR', defaultLanguage: 'ar', supportedLanguages: '["ar", "en", "gdq"]', flagEmoji: '🇴🇲', region: 'Gulf' },
    { countryCode: 'US', countryName: 'United States', nativeName: 'United States', defaultCurrency: 'USD', defaultLanguage: 'en', supportedLanguages: '["en", "es"]', flagEmoji: '🇺🇸', region: 'North America' },
    { countryCode: 'GB', countryName: 'United Kingdom', nativeName: 'United Kingdom', defaultCurrency: 'GBP', defaultLanguage: 'en', supportedLanguages: '["en"]', flagEmoji: '🇬🇧', region: 'Europe' },
    { countryCode: 'DE', countryName: 'Germany', nativeName: 'Deutschland', defaultCurrency: 'EUR', defaultLanguage: 'de', supportedLanguages: '["de", "en"]', flagEmoji: '🇩🇪', region: 'Europe' },
    { countryCode: 'FR', countryName: 'France', nativeName: 'France', defaultCurrency: 'EUR', defaultLanguage: 'fr', supportedLanguages: '["fr", "en"]', flagEmoji: '🇫🇷', region: 'Europe' },
    { countryCode: 'IT', countryName: 'Italy', nativeName: 'Italia', defaultCurrency: 'EUR', defaultLanguage: 'it', supportedLanguages: '["it", "en"]', flagEmoji: '🇮🇹', region: 'Europe' },
    { countryCode: 'ES', countryName: 'Spain', nativeName: 'España', defaultCurrency: 'EUR', defaultLanguage: 'es', supportedLanguages: '["es", "en"]', flagEmoji: '🇪🇸', region: 'Europe' },
    { countryCode: 'CH', countryName: 'Switzerland', nativeName: 'Schweiz / Suisse', defaultCurrency: 'CHF', defaultLanguage: 'de', supportedLanguages: '["de", "fr", "it", "en"]', flagEmoji: '🇨🇭', region: 'Europe' },
    { countryCode: 'CA', countryName: 'Canada', nativeName: 'Canada', defaultCurrency: 'CAD', defaultLanguage: 'en', supportedLanguages: '["en", "fr"]', flagEmoji: '🇨🇦', region: 'North America' },
    { countryCode: 'TR', countryName: 'Turkey', nativeName: 'Türkiye', defaultCurrency: 'TRY', defaultLanguage: 'tr', supportedLanguages: '["tr", "en"]', flagEmoji: '🇹🇷', region: 'Europe' },
    { countryCode: 'EG', countryName: 'Egypt', nativeName: 'جمهورية مصر العربية', defaultCurrency: 'EGP', defaultLanguage: 'ar', supportedLanguages: '["ar", "en"]', flagEmoji: '🇪🇬', region: 'Middle East' },
    { countryCode: 'SO', countryName: 'Somalia', nativeName: 'Jamhuuriyadda Federaalka Soomaaliya', defaultCurrency: 'USD', defaultLanguage: 'so', supportedLanguages: '["so", "ar", "en"]', flagEmoji: '🇸🇴', region: 'Africa' },
    { countryCode: 'ET', countryName: 'Ethiopia', nativeName: 'የኢትዮጵያ ፌዴራላዊ ዴሞክራሲያዊ ሪፐብሊክ', defaultCurrency: 'ETB', defaultLanguage: 'am', supportedLanguages: '["am", "en", "so"]', flagEmoji: '🇪🇹', region: 'Africa' },
    { countryCode: 'MA', countryName: 'Morocco', nativeName: 'المملكة المغربية', defaultCurrency: 'MAD', defaultLanguage: 'ar', supportedLanguages: '["ar", "fr", "en"]', flagEmoji: '🇲🇦', region: 'North Africa' },
    { countryCode: 'DZ', countryName: 'Algeria', nativeName: 'الجمهورية الجزائرية الديمقراطية الشعبية', defaultCurrency: 'DZD', defaultLanguage: 'ar', supportedLanguages: '["ar", "fr", "en"]', flagEmoji: '🇩🇿', region: 'North Africa' },
    { countryCode: 'TN', countryName: 'Tunisia', nativeName: 'الجمهورية التونسية', defaultCurrency: 'TND', defaultLanguage: 'ar', supportedLanguages: '["ar", "fr", "en"]', flagEmoji: '🇹🇳', region: 'North Africa' },
    { countryCode: 'IQ', countryName: 'Iraq', nativeName: 'جمهورية العراق', defaultCurrency: 'IQD', defaultLanguage: 'ar', supportedLanguages: '["ar", "en"]', flagEmoji: '🇮🇶', region: 'Middle East' },
    { countryCode: 'JO', countryName: 'Jordan', nativeName: 'المملكة الأردنية الهاشمية', defaultCurrency: 'JOD', defaultLanguage: 'ar', supportedLanguages: '["ar", "en"]', flagEmoji: '🇯🇴', region: 'Middle East' },
    { countryCode: 'LB', countryName: 'Lebanon', nativeName: 'الجمهورية اللبنانية', defaultCurrency: 'USD', defaultLanguage: 'ar', supportedLanguages: '["ar", "fr", "en"]', flagEmoji: '🇱🇧', region: 'Middle East' },
    { countryCode: 'YE', countryName: 'Yemen', nativeName: 'الجمهورية اليمنية', defaultCurrency: 'USD', defaultLanguage: 'ar', supportedLanguages: '["ar", "en", "gdq"]', flagEmoji: '🇾🇪', region: 'Middle East' },
    { countryCode: 'CN', countryName: 'China', nativeName: '中国', defaultCurrency: 'CNY', defaultLanguage: 'zh', supportedLanguages: '["zh", "en"]', flagEmoji: '🇨🇳', region: 'Asia' },
    { countryCode: 'JP', countryName: 'Japan', nativeName: '日本', defaultCurrency: 'JPY', defaultLanguage: 'ja', supportedLanguages: '["ja", "en"]', flagEmoji: '🇯🇵', region: 'Asia' },
    { countryCode: 'KR', countryName: 'South Korea', nativeName: '대한민국', defaultCurrency: 'KRW', defaultLanguage: 'ko', supportedLanguages: '["ko", "en"]', flagEmoji: '🇰🇷', region: 'Asia' },
    { countryCode: 'RU', countryName: 'Russia', nativeName: 'Россия', defaultCurrency: 'RUB', defaultLanguage: 'ru', supportedLanguages: '["ru", "en"]', flagEmoji: '🇷🇺', region: 'Europe' },
    { countryCode: 'AU', countryName: 'Australia', nativeName: 'Australia', defaultCurrency: 'AUD', defaultLanguage: 'en', supportedLanguages: '["en"]', flagEmoji: '🇦🇺', region: 'Oceania' },
    { countryCode: 'MY', countryName: 'Malaysia', nativeName: 'Malaysia', defaultCurrency: 'MYR', defaultLanguage: 'en', supportedLanguages: '["en", "ar"]', flagEmoji: '🇲🇾', region: 'Asia' },
    { countryCode: 'SG', countryName: 'Singapore', nativeName: 'Singapore', defaultCurrency: 'SGD', defaultLanguage: 'en', supportedLanguages: '["en", "zh"]', flagEmoji: '🇸🇬', region: 'Asia' },
    { countryCode: 'HK', countryName: 'Hong Kong', nativeName: 'Hong Kong', defaultCurrency: 'HKD', defaultLanguage: 'en', supportedLanguages: '["en", "zh"]', flagEmoji: '🇭🇰', region: 'Asia' },
    { countryCode: 'NG', countryName: 'Nigeria', nativeName: 'Nigeria', defaultCurrency: 'NGN', defaultLanguage: 'en', supportedLanguages: '["en", "ar"]', flagEmoji: '🇳🇬', region: 'Africa' },
    { countryCode: 'ZA', countryName: 'South Africa', nativeName: 'South Africa', defaultCurrency: 'ZAR', defaultLanguage: 'en', supportedLanguages: '["en"]', flagEmoji: '🇿🇦', region: 'Africa' },
    { countryCode: 'IN', countryName: 'India', nativeName: 'India', defaultCurrency: 'INR', defaultLanguage: 'en', supportedLanguages: '["en", "ar"]', flagEmoji: '🇮🇳', region: 'Asia' },
    { countryCode: 'PK', countryName: 'Pakistan', nativeName: 'Pakistan', defaultCurrency: 'PKR', defaultLanguage: 'en', supportedLanguages: '["en", "ar"]', flagEmoji: '🇵🇰', region: 'Asia' },
    { countryCode: 'BD', countryName: 'Bangladesh', nativeName: 'Bangladesh', defaultCurrency: 'BDT', defaultLanguage: 'en', supportedLanguages: '["en", "ar"]', flagEmoji: '🇧🇩', region: 'Asia' },
    { countryCode: 'TH', countryName: 'Thailand', nativeName: 'Thailand', defaultCurrency: 'THB', defaultLanguage: 'en', supportedLanguages: '["en"]', flagEmoji: '🇹🇭', region: 'Asia' },
    { countryCode: 'ID', countryName: 'Indonesia', nativeName: 'Indonesia', defaultCurrency: 'IDR', defaultLanguage: 'en', supportedLanguages: '["en", "ar"]', flagEmoji: '🇮🇩', region: 'Asia' },
    { countryCode: 'PH', countryName: 'Philippines', nativeName: 'Philippines', defaultCurrency: 'PHP', defaultLanguage: 'en', supportedLanguages: '["en"]', flagEmoji: '🇵🇭', region: 'Asia' },
    { countryCode: 'VN', countryName: 'Vietnam', nativeName: 'Vietnam', defaultCurrency: 'VND', defaultLanguage: 'en', supportedLanguages: '["en"]', flagEmoji: '🇻🇳', region: 'Asia' },
    { countryCode: 'BR', countryName: 'Brazil', nativeName: 'Brasil', defaultCurrency: 'BRL', defaultLanguage: 'en', supportedLanguages: '["en"]', flagEmoji: '🇧🇷', region: 'South America' },
    { countryCode: 'MX', countryName: 'Mexico', nativeName: 'México', defaultCurrency: 'MXN', defaultLanguage: 'es', supportedLanguages: '["es", "en"]', flagEmoji: '🇲🇽', region: 'North America' },
    { countryCode: 'AR', countryName: 'Argentina', nativeName: 'Argentina', defaultCurrency: 'ARS', defaultLanguage: 'es', supportedLanguages: '["es", "en"]', flagEmoji: '🇦🇷', region: 'South America' },
    { countryCode: 'CL', countryName: 'Chile', nativeName: 'Chile', defaultCurrency: 'CLP', defaultLanguage: 'es', supportedLanguages: '["es", "en"]', flagEmoji: '🇨🇱', region: 'South America' },
    { countryCode: 'CO', countryName: 'Colombia', nativeName: 'Colombia', defaultCurrency: 'COP', defaultLanguage: 'es', supportedLanguages: '["es", "en"]', flagEmoji: '🇨🇴', region: 'South America' },
    { countryCode: 'PE', countryName: 'Peru', nativeName: 'Perú', defaultCurrency: 'PEN', defaultLanguage: 'es', supportedLanguages: '["es", "en"]', flagEmoji: '🇵🇪', region: 'South America' },
    { countryCode: 'NZ', countryName: 'New Zealand', nativeName: 'New Zealand', defaultCurrency: 'NZD', defaultLanguage: 'en', supportedLanguages: '["en"]', flagEmoji: '🇳🇿', region: 'Oceania' },
    { countryCode: 'NL', countryName: 'Netherlands', nativeName: 'Nederland', defaultCurrency: 'EUR', defaultLanguage: 'en', supportedLanguages: '["en", "de"]', flagEmoji: '🇳🇱', region: 'Europe' },
    { countryCode: 'BE', countryName: 'Belgium', nativeName: 'Belgique', defaultCurrency: 'EUR', defaultLanguage: 'fr', supportedLanguages: '["fr", "en", "de"]', flagEmoji: '🇧🇪', region: 'Europe' },
    { countryCode: 'SE', countryName: 'Sweden', nativeName: 'Sverige', defaultCurrency: 'SEK', defaultLanguage: 'en', supportedLanguages: '["en"]', flagEmoji: '🇸🇪', region: 'Europe' },
    { countryCode: 'NO', countryName: 'Norway', nativeName: 'Norge', defaultCurrency: 'NOK', defaultLanguage: 'en', supportedLanguages: '["en"]', flagEmoji: '🇳🇴', region: 'Europe' },
    { countryCode: 'DK', countryName: 'Denmark', nativeName: 'Danmark', defaultCurrency: 'DKK', defaultLanguage: 'en', supportedLanguages: '["en"]', flagEmoji: '🇩🇰', region: 'Europe' },
    { countryCode: 'FI', countryName: 'Finland', nativeName: 'Suomi', defaultCurrency: 'EUR', defaultLanguage: 'en', supportedLanguages: '["en"]', flagEmoji: '🇫🇮', region: 'Europe' },
    { countryCode: 'PL', countryName: 'Poland', nativeName: 'Polska', defaultCurrency: 'PLN', defaultLanguage: 'en', supportedLanguages: '["en"]', flagEmoji: '🇵🇱', region: 'Europe' },
    { countryCode: 'IE', countryName: 'Ireland', nativeName: 'Ireland', defaultCurrency: 'EUR', defaultLanguage: 'en', supportedLanguages: '["en"]', flagEmoji: '🇮🇪', region: 'Europe' },
    { countryCode: 'AT', countryName: 'Austria', nativeName: 'Österreich', defaultCurrency: 'EUR', defaultLanguage: 'de', supportedLanguages: '["de", "en"]', flagEmoji: '🇦🇹', region: 'Europe' },
    { countryCode: 'PT', countryName: 'Portugal', nativeName: 'Portugal', defaultCurrency: 'EUR', defaultLanguage: 'en', supportedLanguages: '["en", "es"]', flagEmoji: '🇵🇹', region: 'Europe' },
    { countryCode: 'GR', countryName: 'Greece', nativeName: 'Ελλάδα', defaultCurrency: 'EUR', defaultLanguage: 'en', supportedLanguages: '["en"]', flagEmoji: '🇬🇷', region: 'Europe' }
];

export const parseLanguagesJson = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
        return [];
    }
};

export const sanitizeCountry = (c) => {
    if (!c) return c;
    const countryCode = String(c.countryCode || '').toUpperCase().trim();
    let defLang = String(c.defaultLanguage || '').toLowerCase().trim();
    if (!isSupportedLanguage(defLang)) {
        defLang = getBestSupportedLanguageForCountry(countryCode);
    }
    let supported = parseLanguagesJson(c.supportedLanguages);
    supported = supported.filter(code => isSupportedLanguage(code));
    if (!supported.includes(defLang)) {
        supported.unshift(defLang);
    }
    if (!supported.includes('en')) {
        supported.push('en');
    }
    if (!supported.includes('ar')) {
        supported.push('ar');
    }

    return {
        ...c,
        countryCode,
        defaultLanguage: defLang,
        supportedLanguages: JSON.stringify(supported)
    };
};

const MarketContext = createContext(null);

export const MarketProvider = ({ children }) => {
    const location = useLocation();
    const navigate = useNavigate();

    // 1. Parse country and language from URL path: /{country}/{language}/...
    const urlSegments = useMemo(() => {
        const pathname = location.pathname.replace(/^\/+|\/+$/g, '');
        if (!pathname) return [];
        return pathname.split('/');
    }, [location.pathname]);

    // Active countries list loaded dynamically from backend or fallback
    const [activeCountries, setActiveCountries] = useState(() => DEFAULT_ACTIVE_COUNTRIES.map(sanitizeCountry));

    useEffect(() => {
        let isMounted = true;
        getActiveCountries().then((res) => {
            if (isMounted && Array.isArray(res) && res.length > 0) {
                const sanitized = res.map(sanitizeCountry);
                const merged = [...sanitized];
                DEFAULT_ACTIVE_COUNTRIES.forEach(def => {
                    if (!merged.some(c => c.countryCode?.toUpperCase() === def.countryCode)) {
                        merged.push(sanitizeCountry(def));
                    }
                });
                setActiveCountries(merged);
            }
        }).catch(() => {
            // Keep fallback defaults silently
        });
        return () => { isMounted = false; };
    }, []);

    // Extract raw parameters from current URL if matching /{country}/{language}/...
    const rawCountryFromUrl = urlSegments[0]?.toLowerCase() || null;
    const rawLangFromUrl = urlSegments[1]?.toLowerCase() || null;

    // Check if the current URL has a valid 2-letter country and language structure
    const isMarketUrl = useMemo(() => {
        if (!rawCountryFromUrl || !rawLangFromUrl) return false;
        const validCountry = /^[a-z]{2}$/.test(rawCountryFromUrl);
        const validLang = /^[a-z]{2,3}(-[a-z]{2})?$/.test(rawLangFromUrl);
        return validCountry && validLang;
    }, [rawCountryFromUrl, rawLangFromUrl]);

    // Read stored user preference fallback
    const storedGeo = useMemo(() => {
        if (typeof window === 'undefined') return null;
        try {
            const raw = localStorage.getItem(GEO_STORAGE_KEY);
            return raw ? JSON.parse(raw) : null;
        } catch (_) {
            return null;
        }
    }, []);

    // Effective Country (lowercase)
    const effectiveCountry = useMemo(() => {
        if (isMarketUrl && rawCountryFromUrl) {
            return rawCountryFromUrl.toLowerCase();
        }
        if (storedGeo?.country) {
            return storedGeo.country.toLowerCase();
        }
        return 'sa';
    }, [isMarketUrl, rawCountryFromUrl, storedGeo]);

    // Effective Country Object
    const currentCountryObj = useMemo(() => {
        const upper = effectiveCountry.toUpperCase();
        const found = activeCountries.find(c => c.countryCode?.toUpperCase() === upper);
        if (found) return found;
        const def = DEFAULT_ACTIVE_COUNTRIES.find(c => c.countryCode?.toUpperCase() === upper);
        if (def) return def;
        const mapping = COUNTRY_DEFAULT_MAPPINGS[upper];
        if (mapping) {
            return {
                countryCode: upper,
                countryName: mapping.name || upper,
                nativeName: mapping.name || upper,
                defaultCurrency: mapping.curr || 'USD',
                defaultLanguage: mapping.lang || 'en',
                supportedLanguages: JSON.stringify([mapping.lang || 'en', 'ar']),
                flagEmoji: '🌐',
                region: 'Global'
            };
        }
        return activeCountries[0] || DEFAULT_ACTIVE_COUNTRIES[0];
    }, [effectiveCountry, activeCountries]);

    // Effective Language
    const effectiveLanguage = useMemo(() => {
        let lang = 'ar';
        if (isMarketUrl && rawLangFromUrl) {
            lang = rawLangFromUrl.toLowerCase();
        } else if (storedGeo?.language) {
            lang = storedGeo.language.toLowerCase();
        } else {
            lang = currentCountryObj?.defaultLanguage?.toLowerCase() || 'ar';
        }
        if (!isSupportedLanguage(lang)) {
            lang = getBestSupportedLanguageForCountry(effectiveCountry);
        }
        return lang;
    }, [isMarketUrl, rawLangFromUrl, storedGeo, currentCountryObj, effectiveCountry]);

    // Effective Currency
    const [explicitCurrency, setExplicitCurrency] = useState(() => {
        if (typeof window === 'undefined') return 'SAR';
        try {
            const raw = localStorage.getItem('luxvono_user_currency');
            if (raw && raw.trim().length === 3) return raw.trim().toUpperCase();
            const stored = localStorage.getItem(GEO_STORAGE_KEY);
            if (stored) {
                const parsed = JSON.parse(stored);
                if (parsed?.currency) return parsed.currency.toUpperCase();
            }
        } catch (_) {}
        return 'SAR';
    });

    const effectiveCurrency = useMemo(() => {
        if (explicitCurrency) return explicitCurrency;
        return currentCountryObj?.defaultCurrency || 'USD';
    }, [explicitCurrency, currentCountryObj]);

    // Supported languages for current market
    const marketSupportedLanguages = useMemo(() => {
        const countryLangCodes = parseLanguagesJson(currentCountryObj?.supportedLanguages);
        if (countryLangCodes.length > 0) {
            return SUPPORTED_LANGUAGES.filter(l => countryLangCodes.includes(l.code));
        }
        const defaultCode = currentCountryObj?.defaultLanguage || 'en';
        return SUPPORTED_LANGUAGES.filter(l => l.code === defaultCode || l.code === 'en' || l.code === 'ar');
    }, [currentCountryObj]);

    // Synchronize HTML lang, dir, and storage when country or language changes
    useEffect(() => {
        if (typeof document === 'undefined') return;

        applyDocumentAttributes(effectiveLanguage);

        // Update localStorage context
        const newGeo = {
            country: effectiveCountry.toUpperCase(),
            language: effectiveLanguage,
            currency: effectiveCurrency,
            userExplicitLanguage: true
        };
        try {
            localStorage.setItem(GEO_STORAGE_KEY, JSON.stringify(newGeo));
            localStorage.setItem('lux_country', effectiveCountry.toUpperCase());
            localStorage.setItem('luxvono_user_explicit_language', effectiveLanguage);
            localStorage.setItem('luxvono_user_currency', effectiveCurrency);
        } catch (_) {}

        window.dispatchEvent(new Event('languageChange'));
        window.dispatchEvent(new Event('geoContextChange'));
    }, [effectiveCountry, effectiveLanguage, effectiveCurrency]);

    // Helper to generate a full market-prefixed path
    const getMarketPath = useCallback((path = '') => {
        if (!path) return `/${effectiveCountry}/${effectiveLanguage}/`;
        const cleanPath = String(path).trim();

        // External or special URLs untouched
        if (cleanPath.startsWith('http://') || cleanPath.startsWith('https://') || cleanPath.startsWith('mailto:') || cleanPath.startsWith('tel:')) {
            return cleanPath;
        }

        // If path already starts with /{country}/{lang}, return as is
        const match = cleanPath.match(/^\/([a-z]{2})\/([a-z]{2,3}(?:-[a-z]{2})?)(\/.*)?$/i);
        if (match) {
            return cleanPath;
        }

        const normalizedSubpath = cleanPath.replace(/^\/+/, '');
        if (!normalizedSubpath) {
            return `/${effectiveCountry}/${effectiveLanguage}/`;
        }
        return `/${effectiveCountry}/${effectiveLanguage}/${normalizedSubpath}`;
    }, [effectiveCountry, effectiveLanguage]);

    // Helper for product path
    const getMarketProductPath = useCallback((productOrSlugOrId) => {
        if (!productOrSlugOrId) return getMarketPath('products');
        let identifier = '';
        if (typeof productOrSlugOrId === 'object') {
            identifier = productOrSlugOrId.slug || productOrSlugOrId.id;
        } else {
            identifier = String(productOrSlugOrId);
        }
        return `/${effectiveCountry}/${effectiveLanguage}/product/${identifier}/`;
    }, [effectiveCountry, effectiveLanguage, getMarketPath]);

    // Helper for category path
    const getMarketCategoryPath = useCallback((catOrSlugOrName) => {
        if (!catOrSlugOrName || catOrSlugOrName === 'all') return getMarketPath('products');
        let identifier = '';
        if (typeof catOrSlugOrName === 'object') {
            identifier = catOrSlugOrName.slug || catOrSlugOrName.name || catOrSlugOrName.id;
        } else {
            identifier = String(catOrSlugOrName);
        }
        return `/${effectiveCountry}/${effectiveLanguage}/category/${identifier}/`;
    }, [effectiveCountry, effectiveLanguage, getMarketPath]);

    // Switch market cleanly without broken reloads
    const switchMarket = useCallback((newCountryCode, newLangCode, newCurrencyCode, customNavigate = null) => {
        const targetCountry = String(newCountryCode || effectiveCountry).toLowerCase().trim();
        let targetLang = String(newLangCode || effectiveLanguage).toLowerCase().trim();
        if (!isSupportedLanguage(targetLang)) {
            targetLang = getBestSupportedLanguageForCountry(targetCountry);
        }
        const targetCurr = String(newCurrencyCode || effectiveCurrency).toUpperCase().trim();

        setExplicitCurrency(targetCurr);

        // Lock user preference
        try {
            localStorage.setItem(MANUAL_LOCK_KEY, 'true');
            localStorage.setItem('luxvono_user_currency', targetCurr);
            localStorage.setItem('luxvono_user_explicit_language', targetLang);
            localStorage.setItem('lux_country', targetCountry.toUpperCase());
            localStorage.setItem(GEO_STORAGE_KEY, JSON.stringify({
                country: targetCountry.toUpperCase(),
                language: targetLang,
                currency: targetCurr,
                userExplicitLanguage: true
            }));
        } catch (_) {}

        // Compute new pathname keeping the current subpath intact
        let subpath = '';
        if (isMarketUrl && urlSegments.length > 2) {
            subpath = '/' + urlSegments.slice(2).join('/');
        } else if (!isMarketUrl && location.pathname !== '/') {
            subpath = location.pathname;
        }

        const destination = `/${targetCountry}/${targetLang}${subpath}${location.search || ''}`;
        const nav = customNavigate || navigate;
        nav(destination);
    }, [effectiveCountry, effectiveLanguage, effectiveCurrency, isMarketUrl, urlSegments, location, navigate]);

    // Currency Formatter
    const formatPrice = useCallback((amount, currencyOverride = null) => {
        const curr = (currencyOverride || effectiveCurrency || 'USD').toUpperCase();
        const num = Number(amount) || 0;
        try {
            return new Intl.NumberFormat(effectiveLanguage === 'ar' ? 'ar-SA' : 'en-US', {
                style: 'currency',
                currency: curr,
                minimumFractionDigits: 0,
                maximumFractionDigits: 2
            }).format(num);
        } catch (_) {
            return `${num.toLocaleString()} ${curr}`;
        }
    }, [effectiveCurrency, effectiveLanguage]);

    const value = useMemo(() => ({
        country: effectiveCountry,
        language: effectiveLanguage,
        currency: effectiveCurrency,
        currentCountry: currentCountryObj,
        activeCountries,
        supportedLanguages: marketSupportedLanguages,
        allSupportedLanguages: SUPPORTED_LANGUAGES,
        isRtl: isRTL(effectiveLanguage),
        isMarketUrl,
        marketPrefix: `/${effectiveCountry}/${effectiveLanguage}`,
        getMarketPath,
        getMarketProductPath,
        getMarketCategoryPath,
        switchMarket,
        formatPrice,
        setCurrency: setExplicitCurrency
    }), [
        effectiveCountry,
        effectiveLanguage,
        effectiveCurrency,
        currentCountryObj,
        activeCountries,
        marketSupportedLanguages,
        isMarketUrl,
        getMarketPath,
        getMarketProductPath,
        getMarketCategoryPath,
        switchMarket,
        formatPrice
    ]);

    return (
        <MarketContext.Provider value={value}>
            {children}
        </MarketContext.Provider>
    );
};

export const useMarket = () => {
    const ctx = useContext(MarketContext);
    if (!ctx) {
        throw new Error('useMarket must be used within a MarketProvider');
    }
    return ctx;
};

export default MarketContext;
